/**
 * Shared rAF typewriter for the living bar + in-page placement collapsed copy.
 * Defaults: ~90ms/char, hold 2s per line, ~3.5s rest on resting copy between cycles.
 * prefers-reduced-motion → static first line, no animation.
 */

export type TypewriterLine = {
  text: string;
  kind: "source" | "page_question" | "invite" | "fallback";
};

export type TypewriterOptions = {
  /** ms per character (default 90). */
  charMs?: number;
  /** Hold fully-typed line (default 2000). */
  holdMs?: number;
  /** Rest on resting/fallback copy between cycles (default 3500). */
  restMs?: number;
  /** Called when a full line is shown (for analytics). */
  onLineShown?: (line: TypewriterLine) => void;
  /** Called every paint with the currently rendered text (may be partial). */
  onRender?: (displayText: string, line: TypewriterLine, complete: boolean) => void;
};

export type TypewriterHandle = {
  /** Replace the queue and restart from the top. */
  setLines: (lines: TypewriterLine[]) => void;
  /** Currently displayed FULL line (not partial) — analytics / display only. */
  currentFullLine: () => TypewriterLine | null;
  /** Whether the current paint is the full line (not mid-type). */
  isComplete: () => boolean;
  stop: () => void;
};

// 90ms/char read as sluggish in QA (a 50-char question took ~4.5s to type,
// so viewers rarely saw line 2) — 45ms still reads as typing, at half the
// wait; the longer hold keeps a fully-typed question readable.
const DEFAULT_CHAR_MS = 45;
const DEFAULT_HOLD_MS = 2600;
const DEFAULT_REST_MS = 3500;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export function createTypewriter(
  getEl: () => HTMLElement | null,
  options: TypewriterOptions = {},
): TypewriterHandle {
  const charMs = options.charMs ?? DEFAULT_CHAR_MS;
  const holdMs = options.holdMs ?? DEFAULT_HOLD_MS;
  const restMs = options.restMs ?? DEFAULT_REST_MS;

  let lines: TypewriterLine[] = [];
  let lineIdx = 0;
  let charIdx = 0;
  let complete = false;
  let raf = 0;
  let lastTick = 0;
  let holdUntil = 0;
  let stopped = false;

  function current(): TypewriterLine | null {
    return lines[lineIdx] ?? null;
  }

  function paint(text: string, line: TypewriterLine, done: boolean) {
    const el = getEl();
    if (el) el.textContent = text;
    options.onRender?.(text, line, done);
  }

  function showFull(line: TypewriterLine) {
    complete = true;
    charIdx = line.text.length;
    paint(line.text, line, true);
    options.onLineShown?.(line);
  }

  function step(now: number) {
    if (stopped) return;
    const line = current();
    if (!line) {
      raf = requestAnimationFrame(step);
      return;
    }

    if (prefersReducedMotion()) {
      // Static first line forever.
      if (!complete) showFull(lines[0] ?? line);
      return;
    }

    if (complete) {
      if (now < holdUntil) {
        raf = requestAnimationFrame(step);
        return;
      }
      // Advance
      const next = (lineIdx + 1) % lines.length;
      const wrapping = next === 0 && lines.length > 1;
      lineIdx = next;
      charIdx = 0;
      complete = false;
      lastTick = now;
      // Extra rest when we cycle back onto resting/fallback
      if (wrapping && (lines[0]?.kind === "fallback" || lines[0]?.kind === "invite")) {
        holdUntil = now + restMs;
        complete = true;
        const restLine = lines[0]!;
        paint(restLine.text, restLine, true);
        raf = requestAnimationFrame(step);
        return;
      }
      raf = requestAnimationFrame(step);
      return;
    }

    if (now - lastTick >= charMs) {
      lastTick = now;
      charIdx = Math.min(charIdx + 1, line.text.length);
      const partial = line.text.slice(0, charIdx);
      paint(partial, line, charIdx >= line.text.length);
      if (charIdx >= line.text.length) {
        complete = true;
        holdUntil = now + holdMs;
        options.onLineShown?.(line);
      }
    }
    raf = requestAnimationFrame(step);
  }

  function start() {
    stopped = false;
    if (typeof cancelAnimationFrame === "function") cancelAnimationFrame(raf);
    if (prefersReducedMotion() && lines[0]) {
      lineIdx = 0;
      showFull(lines[0]);
      return;
    }
    // Non-browser (tests): paint first full line without rAF.
    if (typeof requestAnimationFrame !== "function") {
      if (lines[0]) showFull(lines[0]);
      return;
    }
    lastTick = performance.now();
    raf = requestAnimationFrame(step);
  }

  return {
    setLines(next: TypewriterLine[]) {
      lines = next.filter((l) => l.text.trim().length > 0);
      if (lines.length === 0) {
        lines = [{ text: "Ask me anything", kind: "fallback" }];
      }
      lineIdx = 0;
      charIdx = 0;
      complete = false;
      start();
    },
    currentFullLine: () => current(),
    isComplete: () => complete,
    stop() {
      stopped = true;
      if (typeof cancelAnimationFrame === "function") cancelAnimationFrame(raf);
    },
  };
}
